import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import type { UnsyncedDraft } from '../domain/drafts';
import type { DraftSync } from '../repositories/drafts/draftSync';
import type { SessionEndHold } from '../repositories/drafts/sessionEndHold';

/** The page's drafts and their sync; null while signed out or outside the provider. */
export const DraftSyncContext = createContext<DraftSync | null>(null);

/** The page's drafts and their sync (`DraftOutboxProvider`, mounted at the app root). */
export function useDraftOutbox(): DraftSync {
  const sync = useContext(DraftSyncContext);
  if (!sync) throw new Error('useDraftOutbox needs a signed-in DraftOutboxProvider.');
  return sync;
}

/** The unsynced drafts, oldest first, re-rendering as they change or sync. */
export function useUnsyncedDrafts(): readonly UnsyncedDraft[] {
  const { outbox } = useDraftOutbox();
  const subscribe = useCallback((onChange: () => void) => outbox.subscribe(onChange), [outbox]);
  return useSyncExternalStore(subscribe, () => outbox.drafts());
}

/** One unsynced draft, or undefined once it has synced or been removed. */
export function useUnsyncedDraft(id: string): UnsyncedDraft | undefined {
  return useUnsyncedDrafts().find((d) => d.id === id);
}

/**
 * Drop this page's unsynced drafts, for sign-out and account deletion, which
 * discard them without a warning (FR-009d). A no-op where there are none.
 */
export function useDiscardUnsyncedDrafts(): () => void {
  const sync = useContext(DraftSyncContext);
  return useCallback(() => sync?.discard(), [sync]);
}

/** The page's hold on a session that ended while drafts were unsynced; null outside the provider. */
export const SessionEndHoldContext = createContext<SessionEndHold | null>(null);

/** Whether the page has been signed out but is kept open so its unsynced drafts aren't lost. */
export function useSignedOutHoldingDrafts(): boolean {
  const hold = useContext(SessionEndHoldContext);
  const subscribe = useCallback(
    (onChange: () => void) => hold?.subscribe(onChange) ?? (() => {}),
    [hold],
  );
  return useSyncExternalStore(subscribe, () => hold?.held() ?? false);
}
